import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Grid, Button, TextField,
  Checkbox, Chip, LinearProgress, Dialog, Select, MenuItem,
  InputLabel, FormControl, InputAdornment,
  DialogTitle, DialogContent, DialogActions, IconButton, Alert,
  Snackbar, List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, Tooltip,
} from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery, useMutation } from '@apollo/client';
import {
  MY_WISH_LISTS_QUERY,
  CREATE_WISH_LIST_MUTATION,
  UPDATE_WISH_LIST_MUTATION,
  DELETE_WISH_LIST_MUTATION,
  DUPLICATE_WISH_LIST_MUTATION,
  WISH_LIST_TEMPLATES_QUERY,
} from '../graphql/queries';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const SCALE_OPTIONS  = ['intimate', 'small', 'medium', 'large', 'grand'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'critical'];
const PRIORITY_COLORS = { low: 'default', medium: 'primary', high: 'warning', critical: 'error' };
const SCALE_LABELS = { intimate: 'Intimate (<50)', small: 'Small (50-100)', medium: 'Medium (100-200)', large: 'Large (200-400)', grand: 'Grand (400+)' };

const emptyForm = () => ({ name: '', budget: '', budgetNotes: '', eventScale: '', priority: 'medium', templateId: '' });

const TRADITION_LABELS = {
  iyer: 'Iyer',
  iyengar: 'Iyengar',
  mudaliar: 'Mudaliar',
  vellalar: 'Vellalar / Pillai',
  naidu: 'Naidu',
  general: 'General South Indian',
};

export default function WishlistPage() {
  const [createOpen, setCreateOpen]   = useState(false);
  const [editOpen, setEditOpen]       = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [form, setForm]               = useState(emptyForm());
  const [addItemText, setAddItemText] = useState({});
  const [snack, setSnack]             = useState({ open: false, msg: '', severity: 'success' });

  const { data, loading, error, refetch } = useQuery(MY_WISH_LISTS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: tmplData } = useQuery(WISH_LIST_TEMPLATES_QUERY, {
    variables: {},
    fetchPolicy: 'cache-first',
  });

  const [createWishList, { loading: creating }] = useMutation(CREATE_WISH_LIST_MUTATION, {
    onCompleted: () => { refetch(); setForm(emptyForm()); setCreateOpen(false); showSnack('Wish list created!'); },
    onError: (e) => showSnack(e.message, 'error'),
  });

  const [updateWishList] = useMutation(UPDATE_WISH_LIST_MUTATION, {
    onCompleted: () => { refetch(); if (editOpen) { setEditOpen(false); setEditTarget(null); showSnack('List updated!'); } },
    onError: (e) => showSnack(e.message, 'error'),
  });

  const [deleteWishList] = useMutation(DELETE_WISH_LIST_MUTATION, {
    onCompleted: () => { refetch(); showSnack('List deleted.'); },
    onError: (e) => showSnack(e.message, 'error'),
  });

  const [duplicateWishList] = useMutation(DUPLICATE_WISH_LIST_MUTATION, {
    onCompleted: () => { refetch(); showSnack('List duplicated!'); },
    onError: (e) => showSnack(e.message, 'error'),
  });

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const lists = data?.myWishLists || [];

  const getProgress = (items) => {
    if (!items || !items.length) return 0;
    return Math.round((items.filter(i => i.done).length / items.length) * 100);
  };

  const toggleItem = async (list, idx) => {
    const items = list.items.map((item, i) => i === idx ? { ...item, done: !item.done } : item);
    const isCompleted = items.every(i => i.done);
    await updateWishList({ variables: { id: list.id, items, isCompleted } });
  };

  const addItem = async (list, text) => {
    if (!text.trim()) return;
    const items = [...(list.items || []), { task: text.trim(), done: false }];
    await updateWishList({ variables: { id: list.id, items } });
    setAddItemText(s => ({ ...s, [list.id]: '' }));
  };

  const deleteItem = async (list, idx) => {
    const items = list.items.filter((_, i) => i !== idx);
    await updateWishList({ variables: { id: list.id, items } });
  };

  const handleDeleteList = async (id) => {
    await deleteWishList({ variables: { id } });
  };

  const openEdit = (list) => {
    setEditTarget(list);
    setForm({
      name: list.name,
      budget: list.budget != null ? String(list.budget) : '',
      budgetNotes: list.budgetNotes || '',
      eventScale: list.eventScale || '',
      priority: list.priority || 'medium',
      templateId: '',
    });
    setEditOpen(true);
  };

  const templates = tmplData?.wishlistTemplates || [];

  const handleTemplateSelect = (tmplId) => {
    const tmpl = templates.find(t => t.id === tmplId);
    if (tmpl) {
      setForm(f => ({
        ...f,
        templateId: tmplId,
        name: f.name || tmpl.name,
        eventScale: f.eventScale || tmpl.eventScale || '',
        priority: tmpl.priority || f.priority,
      }));
    } else {
      setForm(f => ({ ...f, templateId: '' }));
    }
  };

  const handleCreate = () => {
    createWishList({ variables: {
      name: form.name,
      budget: form.budget ? parseFloat(form.budget) : null,
      budgetNotes: form.budgetNotes || null,
      eventScale: form.eventScale || null,
      priority: form.priority || 'medium',
      templateId: form.templateId || null,
    }});
  };

  const handleEditSave = () => {
    updateWishList({ variables: {
      id: editTarget.id,
      name: form.name,
      budget: form.budget ? parseFloat(form.budget) : null,
      budgetNotes: form.budgetNotes || null,
      eventScale: form.eventScale || null,
      priority: form.priority || 'medium',
    }});
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {error.message.includes('Forbidden')
            ? 'You do not have permission to access Wish Lists. Please contact the administrator.'
            : error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Wish Lists & Preferences</Typography>
          <Typography variant="body2" color="text.secondary">
            Every family member requirements, all in one organised place
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreateOpen(true)}>
          New List
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: '#fffde7', border: '1px solid #f9a825' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ChecklistIcon sx={{ color: '#f9a825' }} />
          <Typography variant="body2" fontWeight={600} color="text.primary">
            Tip: Let each family stakeholder fill in their own checklist. The wedding organiser can see all lists and ensure nothing is missed.
          </Typography>
        </Stack>
      </Paper>

      {loading && !lists.length && (
        <Stack alignItems="center" py={6}><CircularProgress /></Stack>
      )}

      {!loading && lists.length === 0 && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '2px dashed #e0e0e0' }}>
          <ChecklistIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
          <Typography color="text.secondary">No wish lists yet. Click "New List" to create one.</Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        {lists.map(list => {
          const items    = list.items || [];
          const progress = getProgress(items);
          return (
            <Grid item xs={12} md={6} key={list.id}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="h6" fontWeight={700} flex={1} noWrap>{list.name}</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {list.isCompleted
                      ? <Chip label="Complete" color="success" size="small" />
                      : <Chip label={`${progress}%`} color="primary" size="small" />
                    }
                    <Tooltip title="Edit budget &amp; scale">
                      <IconButton size="small" onClick={() => openEdit(list)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton size="small" onClick={() => duplicateWishList({ variables: { id: list.id } })}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" color="error" onClick={() => handleDeleteList(list.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Budget / Scale / Priority chips */}
                <Stack direction="row" spacing={1} flexWrap="wrap" mb={1.5}>
                  {list.budget != null && (
                    <Chip label={`₹${Number(list.budget).toLocaleString('en-IN')}`} size="small" variant="outlined" color="success" />
                  )}
                  {list.eventScale && (
                    <Chip label={SCALE_LABELS[list.eventScale] || list.eventScale} size="small" variant="outlined" />
                  )}
                  {list.priority && list.priority !== 'medium' && (
                    <Chip label={list.priority.charAt(0).toUpperCase() + list.priority.slice(1)} size="small"
                      color={PRIORITY_COLORS[list.priority] || 'default'} />
                  )}
                </Stack>

                <Box mb={2}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Progress</Typography>
                    <Typography variant="caption" fontWeight={600}>{progress}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={progress}
                    sx={{ borderRadius: 2, height: 8, mt: 0.5 }} />
                </Box>

                <List dense disablePadding>
                  <Paged items={items}>{rows => rows.map((item, idx) => (
                    <ListItem key={idx} disablePadding
                      secondaryAction={
                        <IconButton size="small" onClick={() => deleteItem(list, idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          checked={item.done}
                          onChange={() => toggleItem(list, idx)}
                          icon={<RadioButtonUncheckedIcon />}
                          checkedIcon={<CheckCircleIcon />}
                          color="primary"
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.task}
                        primaryTypographyProps={{
                          variant: 'body2',
                          style: { textDecoration: item.done ? 'line-through' : 'none', color: item.done ? '#9e9e9e' : 'inherit' }
                        }}
                      />
                    </ListItem>
                  ))}</Paged>
                </List>

                <Stack direction="row" spacing={1} mt={2}>
                  <TextField
                    size="small"
                    placeholder="Add new item..."
                    value={addItemText[list.id] || ''}
                    onChange={e => setAddItemText(s => ({ ...s, [list.id]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') addItem(list, addItemText[list.id] || ''); }}
                    sx={{ flex: 1 }}
                  />
                  <Button size="small" variant="outlined"
                    onClick={() => addItem(list, addItemText[list.id] || '')}>
                    Add
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Wish List</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {/* Cultural template picker */}
            {templates.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Start from a cultural template (optional)</InputLabel>
                <Select
                  label="Start from a cultural template (optional)"
                  value={form.templateId}
                  onChange={e => handleTemplateSelect(e.target.value)}
                >
                  <MenuItem value=""><em>Blank list</em></MenuItem>
                  {templates.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                      {t.tradition && ` · ${TRADITION_LABELS[t.tradition] || t.tradition}`}
                      {t.items?.length ? ` (${t.items.length} items)` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {form.templateId && (() => {
              const tmpl = templates.find(t => t.id === form.templateId);
              return tmpl ? (
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#e8f5e9', borderColor: 'success.light' }}>
                  <Typography variant="caption" fontWeight={700} color="success.dark">
                    Template preview — {tmpl.items?.length || 0} items will be pre-loaded
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                    {(tmpl.items || []).slice(0, 3).map(i => i.task).join(' · ')}
                    {(tmpl.items || []).length > 3 ? ` · +${(tmpl.items || []).length - 3} more` : ''}
                  </Typography>
                </Paper>
              ) : null;
            })()}
            <TextField
              label="List Name"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              fullWidth
              helperText="e.g. Bride's Parents List, Extended Family Requests"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Budget Estimate"
                type="number"
                value={form.budget}
                onChange={e => setField('budget', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Priority</InputLabel>
                <Select label="Priority" value={form.priority} onChange={e => setField('priority', e.target.value)}>
                  {PRIORITY_OPTIONS.map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <FormControl fullWidth>
              <InputLabel>Event Scale</InputLabel>
              <Select label="Event Scale" value={form.eventScale} onChange={e => setField('eventScale', e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {SCALE_OPTIONS.map(s => <MenuItem key={s} value={s}>{SCALE_LABELS[s]}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Budget Notes"
              value={form.budgetNotes}
              onChange={e => setField('budgetNotes', e.target.value)}
              multiline rows={2} fullWidth
              placeholder="e.g. Catering ₹20k, Décor ₹15k, Photography ₹10k"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setCreateOpen(false); setForm(emptyForm()); }}>Cancel</Button>
          <Button variant="contained" disabled={creating || !form.name.trim()}
            onClick={handleCreate}>
            {creating ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit budget/scale dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit List Settings</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="List Name"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Budget Estimate"
                type="number"
                value={form.budget}
                onChange={e => setField('budget', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Priority</InputLabel>
                <Select label="Priority" value={form.priority} onChange={e => setField('priority', e.target.value)}>
                  {PRIORITY_OPTIONS.map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <FormControl fullWidth>
              <InputLabel>Event Scale</InputLabel>
              <Select label="Event Scale" value={form.eventScale} onChange={e => setField('eventScale', e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {SCALE_OPTIONS.map(s => <MenuItem key={s} value={s}>{SCALE_LABELS[s]}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Budget Notes"
              value={form.budgetNotes}
              onChange={e => setField('budgetNotes', e.target.value)}
              multiline rows={2} fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.name.trim()} onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
